import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListModuleCatalogueResult,
  type PreviewModuleImpactResult,
} from '@whinerp/client';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-31 Module switchboard.
 *
 * The impact preview is the screen. Turning a module off is one click and
 * removes eleven screens, twenty-three operations and four agent tools, and
 * the person doing it is usually trying to tidy a navigation rail - so they
 * are told exactly what goes, and, above all, that the records stay.
 *
 * "Are you sure?" is not an impact preview. It is a request to guess.
 */

type Catalogue = ListModuleCatalogueResult;
type Impact = PreviewModuleImpactResult;

function ModulesScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ key: string; enabling: boolean } | null>(null);

  const catalogue = useQuery<Catalogue, ApiError>({
    queryKey: ['modules'],
    queryFn: () => api.listModuleCatalogue({}),
  });

  // Fetched when a switch is touched, before it moves. Not after, and not in
  // parallel with the change.
  const impact = useQuery<Impact, ApiError>({
    queryKey: ['module-impact', pending?.key, pending?.enabling],
    queryFn: () =>
      api.previewModuleImpact({
        path: { moduleKey: pending?.key ?? '' },
        body: { enabling: pending?.enabling ?? false },
      }),
    enabled: pending !== null,
  });

  const setState = useMutation({
    mutationFn: (input: { key: string; enabled: boolean }) =>
      api.setModuleState({ path: { flagKey: input.key }, body: { enabled: input.enabled } }),
    onSuccess: () => {
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
      void queryClient.invalidateQueries({ queryKey: ['session'] });
      toast.show({ tone: 'success', title: t('state.saved') });
    },
    onError: (error: ApiError) => {
      // The switch never moved, because nothing moves until this succeeds.
      setPending(null);
      toast.show({ tone: 'error', title: t('state.error'), body: error.message });
    },
  });

  if (catalogue.isPending) return <ModulesLoading />;
  if (catalogue.isError) {
    return <ModulesError error={catalogue.error} retry={() => void catalogue.refetch()} />;
  }

  return (
    <div data-component="ModuleSwitchboard" className="flex w-full flex-col gap-4">
      <header className="flex flex-col gap-1">
        <PageHeading>{t('setup.modules.title')}</PageHeading>
        <p className="text-dense text-ink-meta">{t('setup.modules.body')}</p>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {catalogue.data.modules.map((module) => (
          <Panel key={module.key} data-module-card={module.key}>
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <Icon name={module.icon} size="inline" decorative />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-body text-ink">{module.label}</span>
                  <span className="text-meta text-ink-meta">{module.summary}</span>
                </span>

                {/* Locked is shown, never hidden. Hiding what a plan does not
                    include means nobody discovers the reason to upgrade, and
                    an administrator cannot tell "off" from "not included". */}
                {module.state === 'plan-locked' ? (
                  <span
                    data-plan-lock={module.key}
                    className="inline-flex h-chip items-center gap-1 rounded-chip bg-sunken px-2 text-meta text-ink-meta"
                  >
                    <Icon name="Lock" size="inline" decorative />
                    {t('setup.plan.locked', { values: { plan: module.planMinLabel } })}
                  </span>
                ) : module.core ? (
                  <span className="inline-flex h-chip items-center rounded-chip bg-sunken px-2 text-meta text-ink-meta">
                    {t('setup.impact.core')}
                  </span>
                ) : (
                  <button
                    type="button"
                    role="switch"
                    data-module-switch={module.key}
                    aria-checked={module.state === 'on'}
                    aria-label={module.label}
                    onClick={() => setPending({ key: module.key, enabling: module.state !== 'on' })}
                    className={
                      module.state === 'on'
                        ? 'inline-flex h-touch items-center rounded-control bg-bronze px-3 text-dense text-bronze-on'
                        : 'inline-flex h-touch items-center rounded-control bg-sunken px-3 text-dense text-ink-600'
                    }
                  >
                    {module.state === 'on' ? t('action.on') : t('action.off')}
                  </button>
                )}
              </div>

              {module.features.length > 0 ? (
                <ul className="flex flex-col">
                  {module.features.map((feature) => (
                    <li
                      key={feature.key}
                      data-feature-row={feature.key}
                      className="flex items-center gap-2 border-t border-t-rule py-1 first:border-t-0 text-meta"
                    >
                      <span className="min-w-0 flex-1 truncate text-ink-600">{feature.label}</span>
                      {feature.state === 'plan-locked' ? (
                        <span className="inline-flex items-center gap-1 text-ink-meta">
                          <Icon name="Lock" size="inline" decorative />
                          {t('setup.plan.locked', { values: { plan: feature.planMinLabel } })}
                        </span>
                      ) : (
                        <span className={feature.state === 'on' ? 'text-success-text' : 'text-ink-300'}>
                          {feature.state === 'on' ? t('action.on') : t('action.off')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </Panel>
        ))}
      </div>

      {/* The preview, before the switch moves. */}
      {pending ? (
        <ConfirmDialog
          open
          title={pending.enabling ? t('setup.impact.titleOn') : t('setup.impact.title')}
          confirmLabel={
            pending.enabling
              ? t('setup.impact.confirmOn', {
                  values: { module: impact.data?.moduleLabel ?? pending.key },
                })
              : t('setup.impact.confirmOff', {
                  values: { module: impact.data?.moduleLabel ?? pending.key },
                })
          }
          confirmIntent={pending.enabling ? 'primary' : 'danger'}
          labels={{ cancel: t('action.cancel') }}
          busy={setState.isPending || impact.isPending}
          onConfirm={() => setState.mutate({ key: pending.key, enabled: pending.enabling })}
          onCancel={() => setPending(null)}
        >
          {impact.isPending ? (
            <Skeleton className="h-block-md w-full" />
          ) : impact.data ? (
            <div data-impact-preview className="flex flex-col gap-2">
              <ul className="flex flex-col gap-1">
                <li>
                  <Num>
                    {t('setup.impact.features', {
                      values: { count: formatCount(impact.data.features.length, format) },
                    })}
                  </Num>
                </li>
                <li>
                  <Num>
                    {t('setup.impact.screens', {
                      values: { count: formatCount(impact.data.screens.length, format) },
                    })}
                  </Num>
                </li>
                <li>
                  <Num>
                    {t('setup.impact.operations', {
                      values: { count: formatCount(impact.data.operations.length, format) },
                    })}
                  </Num>
                </li>
                <li>
                  <Num>
                    {t('setup.impact.tools', {
                      values: { count: formatCount(impact.data.agentTools.length, format) },
                    })}
                  </Num>
                </li>
              </ul>

              {impact.data.dependents.length > 0 ? (
                <p className="flex items-start gap-2 text-warning-text">
                  <Icon name="Warning" size="inline" decorative />
                  {t('setup.impact.dependents', {
                    values: { modules: impact.data.dependents.map((d) => d.label).join(', ') },
                  })}
                </p>
              ) : null}

              {/* The sentence people are looking for, with numbers behind it.
                  "Your data is safe" is the kind of reassurance that is ignored
                  precisely when it matters. */}
              {impact.data.retained.length > 0 ? (
                <div data-impact-retained className="flex flex-col gap-1 rounded-card bg-sunken p-2">
                  <SectionLabel>{t('setup.impact.retained')}</SectionLabel>
                  <ul className="flex flex-col">
                    {impact.data.retained.map((entry) => (
                      <li key={entry.objectKey} className="flex items-baseline gap-2 text-dense text-ink">
                        {/* A count and a label, not a sentence. A message key
                            holding only placeholders is not a message - there
                            is nothing in it to translate, and the Arabic check
                            was right to say so. */}
                        <Num>{formatCount(entry.count, format)}</Num>
                        <span>{entry.label.toLowerCase()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : (
            <ErrorState
              title={t('state.error')}
              body={impact.error?.message ?? ''}
              onRetry={() => void impact.refetch()}
            />
          )}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function ModulesEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="ToggleLeft"
      title={t('setup.modules.title')}
      body={t('setup.modules.body')}
      primary={{ label: t('empty.action.openModules'), icon: 'ToggleLeft', href: '/setup/modules' }}
    />
  );
}

function ModulesLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-lg" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-block-md w-full" />
        ))}
      </div>
    </div>
  );
}

function ModulesError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Modules could not be loaded"
      body={error instanceof Error ? error.message : 'The switchboard did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-31',
  route: '/setup/modules',
  surface: 'page',
  title: 'Modules',
  features: ['F-013'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'setup', section: 'modules' }],
  components: [],
  elements: ['ModuleSwitchboard'],
  // Both appear only when a switch is touched, which is the whole design: the
  // preview comes before the change, not after it. check:screens requires a UI
  // test to have driven the product to the point where they render.
  afterInteraction: ['ImpactPreview', 'ConfirmDialog'],
  states: {
    empty: ModulesEmpty,
    loading: ModulesLoading,
    error: ModulesError,
  },
  component: ModulesScreen,
});

export default screen;
